/**
 * Skeleton loading screen for the login page.
 *
 * The body — brand logo, preview image, heading, subheading, and the stack of
 * provider buttons, all centred — is kept separate from the chrome around it,
 * so that an in-page loading state could reuse it alone.
 * `SkeletonLoginScreen` composes the two, wrapping the body in the
 * highlight-background `ConversationScreen` the page loads into, under a nav
 * bar with no avatar — the login route's stack sets `headerRight: () => null`.
 */

import { useTheme } from '@repo/theme';
import { Pad, PadBox } from 'component/basics';
import { spacings } from 'component/constants';
import { BaseSkeleton } from 'component/progress/base-skeleton';
import { ButtonSkeleton } from 'component/progress/button-skeleton';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { NavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { ConversationScreen } from 'component/screen';
import { StyleSheet, View } from 'react-native';
import { useIsDesktopBreakpoint } from 'util/layout';

const PROVIDER_BUTTON_COUNT = 2;

function LoginBodySkeleton() {
    return (
        <View style={LoginBodySkeletonStyle.column}>
            <BaseSkeleton borderRadius={4} height={40} width={67} />
            <Pad size={32} />
            <BaseSkeleton borderRadius={spacings.sm} height={215} width={335} />
            <Pad size={32} />
            <TextContainerSkeleton borderRadius={4} height={14} width={240} />
            <Pad size={12} />
            <TextContainerSkeleton borderRadius={2} height={6} width={300} />
            <Pad size={32} />
            {Array.from({ length: PROVIDER_BUTTON_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={i}>
                    {i > 0 && <Pad size={12} />}
                    <ButtonSkeleton width={335} />
                </View>
            ))}
        </View>
    );
}

export function SkeletonLoginScreen() {
    const { colors } = useTheme();
    const isDesktop = useIsDesktopBreakpoint();
    return (
        <>
            <NavBarSkeleton large={isDesktop} withAvatar={false} />
            <ConversationScreen backgroundColor={colors.colorSurfaceHighlight} noWrap>
                <LoadingAnnouncement />
                <PadBox horiz={40}>
                    <Pad size={80} />
                    <LoginBodySkeleton />
                    <Pad size={80} />
                </PadBox>
            </ConversationScreen>
        </>
    );
}

const LoginBodySkeletonStyle = StyleSheet.create({
    column: { alignItems: 'center' },
});
